import Box from "@mui/material/Box";
import {
    SurveyAttentionType,
    SurveyReviewState,
    type SurveySubmissionListRequestDTO
} from "crqlar-react-common/dist/models/survey/surveySubmission.model";
import {SurveyAggregationService} from "crqlar-react-common/dist/services/aggregation/surveyAggregation.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import type {DateRange} from "../../common/newDatePicker/datePickerShared";
import {
    ServerTable,
    ServerTableFrame,
    ServerTableProvider,
    ServerTableRailColumns,
    ServerTableRailFilter,
    ServerTableRailSearch,
    ServerTableRailSort,
} from "../../common/serverTable";
import {SURVEY_REVIEW_STATE_LABEL_KEYS} from "../../common/uiElements/SurveyReviewStateElement";
import {insightsLoadingOf} from "../../insights/kit/insightsLoading";
import {StatCard} from "../../insights/kit/StatCard";
import InsightsDateRangeControl from "../../insights/v2/base/InsightsDateRangeControl";
import {
    RightSidebarDivider,
    RightSidebarRail,
    RightSidebarRailHeader,
    RightSidebarRailHeaderCenter,
    RightSidebarRailLayout,
} from "../../layout/rightSidebar/RightSidebarRail";
import SurveyInvitationsRailButton from "../invitations/SurveyInvitationsRailButton";
import SurveyInviteRailButton from "../share/SurveyInviteRailButton";
import {SURVEY_MODULE_SUBMISSIONS_TABLE_ID, SURVEY_SUBMISSION_COUNTS_QUERY_KEY} from "../shared/surveyQueryRefresh";
import {toInclusiveDayRange, useUrlDateRange} from "../shared/useUrlDateRange";
import {SurveyListSubmissionDialog} from "../submissions/SurveyListSubmissionDialog";
import {useSurveySubmissionsList} from "../submissions/useSurveySubmissionsList";

const ATTENTION_TYPES = Object.values(SurveyAttentionType);

/**
 * Every reply in the period, open and resolved, to read and resolve one by one - no filter until someone
 * sets one. The full history with its exports lives in the settings.
 */
export default function SurveysSubmissionsTab() {
    useTrackScreenVisit('surveys submissions');

    const {range, setRange} = useUrlDateRange();

    const {
        controller,
        filterOptions,
        selectedSubmission,
        nextOpenSubmissionId,
        openSubmission,
        closeSubmission
    } = useSurveySubmissionsList({
        tableId: SURVEY_MODULE_SUBMISSIONS_TABLE_ID,
        range,
        reviewColumns: true,
    });

    return (
        <>
            <Box sx={{display: "flex", flex: 1, minWidth: 0, minHeight: 0}}>
                <ServerTableProvider controller={controller}>
                    <ServerTableFrame>
                        <RightSidebarRailLayout>
                            <RightSidebarRailHeader>
                                <RightSidebarRailHeaderCenter>
                                    <InsightsDateRangeControl
                                        range={range}
                                        compareRange={null}
                                        onChange={selection => setRange(selection.range)}
                                        comparison={false}
                                        allTime
                                    />
                                </RightSidebarRailHeaderCenter>
                            </RightSidebarRailHeader>

                            <SubmissionCounts range={range}/>

                            <ServerTable/>

                            <RightSidebarRail>
                                <ServerTableRailSearch/>
                                <ServerTableRailSort/>
                                <ServerTableRailFilter options={filterOptions}/>
                                <ServerTableRailColumns/>
                                <RightSidebarDivider/>
                                <SurveyInvitationsRailButton range={range}/>
                                <SurveyInviteRailButton/>
                            </RightSidebarRail>
                        </RightSidebarRailLayout>
                    </ServerTableFrame>
                </ServerTableProvider>
            </Box>

            {!!selectedSubmission && (
                <SurveyListSubmissionDialog
                    submission={selectedSubmission}
                    onClose={closeSubmission}
                    nextOpenSubmissionId={nextOpenSubmissionId}
                    onOpenSubmission={openSubmission}
                />
            )}
        </>
    );
}

type CountRequest = Pick<SurveySubmissionListRequestDTO, 'reviewStates' | 'attentionTypes'>;

const formatCount = (value: number | undefined) => (value == null ? '—' : String(value));

/*
    The period's replies by state, each the list request with a page of one. They follow the date control
    only - not the list's search or filters - and are figures, not shortcuts into the list.
*/
function SubmissionCounts({range}: {range: DateRange}) {
    const {t} = useTranslation();
    const submittedAt = toInclusiveDayRange(range);

    const countsQuery = useQuery(
        [SURVEY_SUBMISSION_COUNTS_QUERY_KEY, submittedAt.start.getTime(), submittedAt.end.getTime()],
        async () => {
            const count = (request: CountRequest) => SurveyAggregationService.listSubmissions({
                ...request,
                submittedAt,
                limit: {offset: 0, limit: 1},
            }).then(page => page.totalElements ?? 0);

            const [open, unhappy, resolved] = await Promise.all([
                count({reviewStates: [SurveyReviewState.OPEN]}),
                count({reviewStates: [SurveyReviewState.OPEN], attentionTypes: ATTENTION_TYPES}),
                count({reviewStates: [SurveyReviewState.REVIEWED]}),
            ]);

            return {open, unhappy, resolved};
        },
        {keepPreviousData: true, refetchOnWindowFocus: false}
    );
    const counts = countsQuery.data;
    const loading = insightsLoadingOf(countsQuery.isLoading, countsQuery.isPreviousData);

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: {xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))'},
                gap: 1.5,
                px: 2,
                pb: 2,
            }}
        >
            <StatCard
                loading={loading}
                data={{
                    id: 'open',
                    // The review state's own key: a bare "Open" translates as the verb.
                    title: t(SURVEY_REVIEW_STATE_LABEL_KEYS[SurveyReviewState.OPEN]),
                    hint: t('Replies from this period nobody has resolved yet.'),
                    value: formatCount(counts?.open),
                }}
            />

            <StatCard
                loading={loading}
                data={{
                    id: 'unhappy',
                    title: t('Unhappy guests'),
                    hint: t('Open replies from this period with a low score or a negative comment.'),
                    value: formatCount(counts?.unhappy),
                }}
            />

            <StatCard
                loading={loading}
                data={{
                    id: 'resolved',
                    title: t(SURVEY_REVIEW_STATE_LABEL_KEYS[SurveyReviewState.REVIEWED]),
                    hint: t('Replies from this period that are resolved.'),
                    value: formatCount(counts?.resolved),
                }}
            />
        </Box>
    );
}
